import { useEffect } from "react";
import { UseFormSetValue } from "react-hook-form";
import { CourseForm } from "../types/forms/course-form";
import { PaymentType } from "../types/records/type-payment-record";
import { codePostalDepart, estDansBruxelles } from "../utils/zone-paiement";

/** Valeur du champ tant qu'aucun moyen de paiement n'est retenu. */
export const PAYMENT_TYPE_NON_SELECTIONNE = -1;

type UsePaymentAvailabilityParams = {
  arrets?: CourseForm["arrets"];
  paymentType: number;
  setValue: UseFormSetValue<CourseForm>;
  /**
   * Faux en édition : le moyen de paiement d'une course existante est figé (il
   * n'est même pas renvoyé au serveur), la sélection ne doit donc pas bouger.
   */
  actif?: boolean;
};

/**
 * Le paiement à bord n'est encaissé qu'à la prise en charge : si le client ne
 * se présente pas, le chauffeur s'est déplacé pour rien. Il n'est donc offert
 * qu'au départ de Bruxelles.
 *
 * Même attitude que le back (`ZoneGeographique.EstDansBruxelles`) : tant que le
 * code postal de départ n'est pas un code postal bruxellois connu - y compris
 * quand il est absent ou non déterminable - l'option n'est pas autorisée. Le
 * front ne doit jamais être plus permissif que le back, sous peine d'afficher
 * un choix que l'envoi refusera (erreur 13023).
 */
export const usePaymentAvailability = ({
  arrets,
  paymentType,
  setValue,
  actif = true,
}: UsePaymentAvailabilityParams) => {
  const codePostal = codePostalDepart(arrets);
  const paiementABordAutorise = estDansBruxelles(codePostal);

  // Le départ peut sortir de la zone après coup : la sélection devenue
  // impossible est reprise, pour que l'utilisateur en choisisse une autre
  // plutôt que de se faire refuser à l'envoi.
  useEffect(() => {
    if (actif && !paiementABordAutorise && paymentType === PaymentType.ABord) {
      setValue("paymentType", PAYMENT_TYPE_NON_SELECTIONNE);
    }
  }, [actif, paiementABordAutorise, paymentType, setValue]);

  return { paiementABordAutorise };
};
